import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ExternalLink, FileText, Pencil, Copy, Archive, Upload, Trash2, ImageIcon } from "lucide-react";

const FILE_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/files`;
const money = (v) => (v == null || v === "" ? "—" : `$${Number(v).toLocaleString("en-AU", { minimumFractionDigits: 2 })}`);
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

const Row = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-zinc-200 text-sm">
    <span className="text-zinc-500">{label}</span>
    <span className="text-zinc-900 text-right font-medium">{children || "—"}</span>
  </div>
);

export default function ProductDrawer({ open, onOpenChange, product, onEdit, onChanged }) {
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const [usedIn, setUsedIn] = useState([]);
  useEffect(() => {
    if (open && product) api.get(`/library/products/${product.product_id}/used-in`).then((r) => setUsedIn(r.data)).catch(() => setUsedIn([]));
  }, [open, product]);
  if (!product) return null;
  const imgs = product.images || [];
  const hero = imgs[0];

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onChanged(); } catch { toast.error("Action failed"); } };
  const addPhoto = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/library/products/${product.product_id}/images`, fd); toast.success("Photo added"); onChanged(); }
    catch { toast.error("Upload failed"); }
  };
  const specHref = product.spec_sheet_path ? `${FILE_BASE}/${product.spec_sheet_path}` : product.spec_sheet_url;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[560px] overflow-y-auto p-0" data-testid="product-drawer">
        <div className="p-5">
          <SheetHeader className="space-y-1 text-left">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-zinc-400">
              <span>{product.category}</span>
              {product.status === "archived" && <span className="text-amber-600">· Archived</span>}
            </div>
            <SheetTitle className="font-heading text-2xl font-black tracking-tighter">{product.name}</SheetTitle>
            <p className="text-sm text-zinc-500">{[product.supplier, product.code].filter(Boolean).join(" · ") || "—"}</p>
          </SheetHeader>

          {/* Gallery */}
          <div className="mt-4">
            {hero ? (
              <img src={`${FILE_BASE}/${hero.path}`} alt={hero.name} className="w-full h-60 object-cover rounded-sm border border-zinc-200" data-testid="drawer-hero" />
            ) : (
              <div className="w-full h-60 bg-zinc-100 rounded-sm flex items-center justify-center text-zinc-400"><ImageIcon className="h-8 w-8" /></div>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              {imgs.map((im) => (
                <div key={im.file_id} className="relative group">
                  <img src={`${FILE_BASE}/${im.path}`} alt={im.name} className="h-14 w-14 object-cover rounded-sm border border-zinc-200" />
                  <button onClick={() => act(() => api.delete(`/library/products/${product.product_id}/images/${im.file_id}`), "Photo removed")}
                    className="absolute -top-1.5 -right-1.5 bg-white border border-zinc-200 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`drawer-del-img-${im.file_id}`}>
                    <Trash2 className="h-3 w-3 text-rose-600" />
                  </button>
                </div>
              ))}
              <input ref={fileRef} type="file" accept="image/*" capture className="hidden" data-testid="drawer-photo-input"
                onChange={(e) => { addPhoto(e.target.files[0]); e.target.value = ""; }} />
              <button onClick={() => fileRef.current?.click()} className="h-14 w-14 rounded-sm border border-dashed border-zinc-300 flex items-center justify-center text-zinc-400 hover:bg-zinc-50" data-testid="drawer-add-photo">
                <Upload className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            {product.supplier_url && (
              <Button asChild variant="outline" className="rounded-sm gap-1.5 flex-1" data-testid="drawer-supplier-link">
                <a href={product.supplier_url} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" /> Open supplier page</a>
              </Button>
            )}
            {specHref && (
              <Button asChild variant="outline" className="rounded-sm gap-1.5 flex-1" data-testid="drawer-spec-link">
                <a href={specHref} target="_blank" rel="noreferrer"><FileText className="h-4 w-4" /> Spec sheet</a>
              </Button>
            )}
          </div>

          {/* Details */}
          <div className="mt-5">
            <Row label="Supplier">{product.supplier}</Row>
            <Row label="Brand">{product.brand}</Row>
            <Row label="Code">{product.code}</Row>
            <Row label="Colour">{product.colour}</Row>
            <Row label="Size">{product.size}</Row>
            <Row label="Unit">{product.unit}</Row>
            <Row label="Cost ex GST">{money(product.cost_ex_gst)}</Row>
            <Row label="Lead time">{product.lead_time_weeks != null ? `${product.lead_time_weeks} wk` : "—"}</Row>
            <Row label="Added">{fmt(product.created_at)}</Row>
            <Row label="Updated">{fmt(product.updated_at)}</Row>
          </div>

          {product.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4">
              {product.tags.map((t) => <span key={t} className="text-xs bg-zinc-100 text-zinc-600 rounded-sm px-2 py-0.5">{t}</span>)}
            </div>
          )}

          <div className="mt-5">
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Used in</p>
            {usedIn.length > 0 ? (
              <div className="space-y-1">
                {usedIn.map((u, i) => (
                  <button key={`${u.project_id}-${u.kind}-${u.code}`} onClick={() => navigate(`/project/${u.project_id}`)} data-testid={`used-in-${i}`}
                    className="block text-left text-sm text-zinc-700 hover:text-zinc-900 hover:underline">
                    {u.project_name} <span className="text-zinc-400 text-xs">· {u.kind === "ffe" ? "FF&E" : "Material"} · {u.code} · {u.status}</span>
                  </button>
                ))}
              </div>
            ) : <p className="text-sm text-zinc-500">Not used in any project yet</p>}
          </div>

          {product.notes && (
            <div className="mt-5">
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Notes</p>
              <p className="text-sm text-zinc-700 whitespace-pre-wrap">{product.notes}</p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-zinc-200 p-4 flex gap-2">
          <Button onClick={() => onEdit(product)} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm gap-1.5" data-testid="drawer-edit"><Pencil className="h-4 w-4" /> Edit</Button>
          <Button variant="outline" className="rounded-sm gap-1.5" data-testid="drawer-duplicate"
            onClick={() => act(() => api.post(`/library/products/${product.product_id}/duplicate`), "Duplicated")}><Copy className="h-4 w-4" /> Duplicate</Button>
          {product.status === "archived" ? (
            <Button variant="outline" className="rounded-sm gap-1.5 ml-auto" data-testid="drawer-unarchive"
              onClick={() => act(() => api.post(`/library/products/${product.product_id}/unarchive`), "Restored")}><Archive className="h-4 w-4" /> Restore</Button>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="rounded-sm gap-1.5 ml-auto text-amber-700 border-amber-200 hover:bg-amber-50" data-testid="drawer-archive"><Archive className="h-4 w-4" /> Archive</Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-sm">
                <AlertDialogHeader>
                  <AlertDialogTitle>Archive {product.name}?</AlertDialogTitle>
                  <AlertDialogDescription>It will be hidden from the active library but kept for your records.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                  <AlertDialogAction className="rounded-sm bg-amber-600 hover:bg-amber-700"
                    onClick={() => act(() => api.post(`/library/products/${product.product_id}/archive`), "Archived")}>Archive</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
